import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Snackbar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import MainLayout from '../../../../components/Layout/MainLayout';
import { useTranslation } from 'react-i18next';
import {
  getWarningEmailBlastHistory,
  getWarningEmailBlastHtml,
  sendWarningEmailBlastNow,
  WarningEmailBlastHistoryItem
} from './warning-email-blast.api';

const WarningEmailBlast: React.FC = () => {
  const { t } = useTranslation();

  const [tabIndex, setTabIndex] = useState(0);

  const [html, setHtml] = useState<string>('');
  const [manageOpen, setManageOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [emails, setEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyItems, setHistoryItems] = useState<WarningEmailBlastHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(50);
  const [viewHtmlOpen, setViewHtmlOpen] = useState(false);
  const [viewHtmlLoading, setViewHtmlLoading] = useState(false);
  const [viewHtml, setViewHtml] = useState('');
  const [viewHtmlGuid, setViewHtmlGuid] = useState<string | null>(null);
  const viewHtmlAbortRef = useRef<AbortController | null>(null);
  const viewHtmlCacheRef = useRef<Map<string, string>>(new Map());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const previewHtml = useMemo(() => html || '', [html]);

  const loadHistory = useCallback(async (pageNumber: number, pageSize: number) => {
    try {
      setHistoryLoading(true);
      setHistoryError(null);
      const res = await getWarningEmailBlastHistory(pageNumber, pageSize);
      const ok = res?.success === true && res?.data != null;
      if (!ok) {
        setHistoryItems([]);
        setHistoryTotal(0);
        setHistoryError(t('warning_email_blast_history_load_error'));
        return;
      }

      setHistoryItems(Array.isArray(res.data.items) ? res.data.items : []);
      setHistoryTotal(Number(res.data.total) || 0);
    } catch (err: any) {
      const apiMsg = err?.response?.data?.errors?.[0]?.errorMessage;
      setHistoryItems([]);
      setHistoryTotal(0);
      setHistoryError(apiMsg || t('warning_email_blast_history_load_error'));
    } finally {
      setHistoryLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (tabIndex !== 1) return;
    loadHistory(historyPage + 1, historyRowsPerPage);
  }, [tabIndex, historyPage, historyRowsPerPage, loadHistory]);

  const handleSendNow = async () => {
    try {
      setSending(true);
      const res = await sendWarningEmailBlastNow({
        subject,
        emails,
        html
      });

      const ok = res?.success === true && res?.data === true;
      setSnackbar({
        open: true,
        message: ok ? t('warning_email_blast_success') : t('warning_email_blast_error_default'),
        severity: ok ? 'success' : 'error'
      });

      if (ok) {
        setManageOpen(false);
      }
    } catch (err: any) {
      const apiMsg = err?.response?.data?.errors?.[0]?.errorMessage;
      setSnackbar({
        open: true,
        message: apiMsg || t('warning_email_blast_error_default'),
        severity: 'error'
      });
    } finally {
      setSending(false);
    }
  };

  const handleViewHtml = async (dispatchGuid: string) => {
    try {
      viewHtmlAbortRef.current?.abort();

      setViewHtmlGuid(dispatchGuid);
      setViewHtmlOpen(true);
      const cachedHtml = viewHtmlCacheRef.current.get(dispatchGuid);
      if (cachedHtml != null) {
        setViewHtml(cachedHtml);
        setViewHtmlLoading(false);
        return;
      }

      setViewHtmlLoading(true);
      setViewHtml('');

      const controller = new AbortController();
      viewHtmlAbortRef.current = controller;

      const res = await getWarningEmailBlastHtml(dispatchGuid, controller.signal);
      const ok = res?.success === true && res?.data?.html != null;
      if (!ok) {
        setSnackbar({
          open: true,
          message: t('warning_email_blast_html_load_error'),
          severity: 'error'
        });
        return;
      }
      viewHtmlCacheRef.current.set(dispatchGuid, res.data.html);
      setViewHtml(res.data.html);
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return;
      }

      const apiMsg = err?.response?.data?.errors?.[0]?.errorMessage;
      setSnackbar({
        open: true,
        message: apiMsg || t('warning_email_blast_html_load_error'),
        severity: 'error'
      });
    } finally {
      setViewHtmlLoading(false);
      viewHtmlAbortRef.current = null;
    }
  };

  const handleCancelViewHtml = useCallback(() => {
    viewHtmlAbortRef.current?.abort();
    viewHtmlAbortRef.current = null;
    setViewHtmlLoading(false);
    setViewHtmlOpen(false);
    setViewHtml('');
    setViewHtmlGuid(null);
  }, []);

  const handleDownloadViewHtml = useCallback(() => {
    if (!viewHtml) return;

    const fileName = `warning-email-blast-${viewHtmlGuid || 'preview'}.html`;
    const blob = new Blob([viewHtml], { type: 'text/html;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  }, [viewHtml, viewHtmlGuid]);

  return (
    <MainLayout containerMaxWidth="xl">
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
          {t('warning_email_blast_title')}
        </Typography>

        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
          <Tab label={t('warning_email_blast_tab_send')} />
          <Tab label={t('warning_email_blast_tab_history')} />
        </Tabs>

        {tabIndex === 0 && (
          <Grid container spacing={2}>
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('warning_email_blast_html_label')}
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                multiline
                minRows={20}
              />
            </Grid>

            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, minHeight: 420 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('warning_email_blast_preview_title')}
                </Typography>
                <Box sx={{ overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </Paper>

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => setManageOpen(true)}
                  disabled={sending}
                >
                  {t('warning_email_blast_manage_button')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}

        {tabIndex === 1 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            {historyLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">{t('warning_email_blast_loading')}</Typography>
              </Box>
            )}

            {!historyLoading && historyError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {historyError}
              </Alert>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('warning_email_blast_history_col_date')}</TableCell>
                  <TableCell>{t('warning_email_blast_history_col_subject')}</TableCell>
                  <TableCell>{t('warning_email_blast_history_col_to')}</TableCell>
                  <TableCell>{t('warning_email_blast_history_col_bcc')}</TableCell>
                  <TableCell>{t('warning_email_blast_history_col_status')}</TableCell>
                  <TableCell align="right">{t('warning_email_blast_history_col_actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyItems.map((row) => (
                  <TableRow key={row.dispatchGuid}>
                    <TableCell>
                      {row.createdDate ? new Date(row.createdDate).toLocaleString('pt-BR') : '-'}
                    </TableCell>
                    <TableCell>{row.subject || '-'}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 360,
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        lineHeight: 1.3
                      }}
                    >
                      {row.addressDestiny || '-'}
                    </TableCell>
                    <TableCell>{row.bccEmails || '-'}</TableCell>
                    <TableCell>
                      {row.sentSuccess === true
                        ? t('warning_email_blast_status_success')
                        : row.sentSuccess === false
                          ? t('warning_email_blast_status_failed')
                          : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewHtml(row.dispatchGuid)}
                      >
                        {t('warning_email_blast_view_html')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

                {historyItems.length === 0 && !historyLoading && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2">{t('warning_email_blast_history_empty')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={historyTotal}
              page={historyPage}
              onPageChange={(_, newPage) => setHistoryPage(newPage)}
              rowsPerPage={historyRowsPerPage}
              onRowsPerPageChange={(e) => {
                const next = parseInt(e.target.value, 10);
                setHistoryRowsPerPage(Number.isFinite(next) ? next : 50);
                setHistoryPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              labelRowsPerPage={t('warning_email_blast_history_rows_per_page')}
            />
          </Paper>
        )}

        <Dialog open={manageOpen} onClose={() => setManageOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{t('warning_email_blast_modal_title')}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label={t('warning_email_blast_subject_label')}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                inputProps={{ maxLength: 80 }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label={t('warning_email_blast_emails_label')}
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                inputProps={{ maxLength: 500 }}
                multiline
                minRows={3}
              />
              <Box sx={{ mt: 2 }}>
                <Alert severity="info">
                  {t('warning_email_blast_emails_help')}
                </Alert>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManageOpen(false)} disabled={sending}>
              {t('warning_email_blast_cancel')}
            </Button>
            <Button onClick={handleSendNow} variant="contained" disabled={sending}>
              {t('warning_email_blast_send_now')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={viewHtmlOpen}
          onClose={handleCancelViewHtml}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>{t('warning_email_blast_html_modal_title')}</DialogTitle>
          <DialogContent>
            {viewHtmlLoading && (
              <Box sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">{t('warning_email_blast_loading')}</Typography>
                </Box>
                <LinearProgress />
              </Box>
            )}
            {!viewHtmlLoading && (
              <Box sx={{ border: '1px solid #eee', height: 520 }}>
                <iframe
                  title={viewHtmlGuid ?? 'warning-email-blast-html'}
                  sandbox=""
                  srcDoc={viewHtml || ''}
                  style={{ width: '100%', height: '100%', border: 0 }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {viewHtmlLoading ? (
              <Button onClick={handleCancelViewHtml}>
                {t('warning_email_blast_cancel')}
              </Button>
            ) : (
              <>
                <Button onClick={handleDownloadViewHtml} disabled={!viewHtml}>
                  {t('warning_email_blast_download_html')}
                </Button>
                <Button onClick={handleCancelViewHtml}>
                  {t('warning_email_blast_close')}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
};

export default WarningEmailBlast;
